import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import {
	Activity,
	ActivityCreatePayload,
	ActivityText,
	ActivityUpdatePayload,
	BaseText,
	Content,
} from '@mira-avant/mtlibs';
import { LanguageService } from './language.service';
import { ContentService } from './content.service';
import { ApiService } from './api.service';

@Injectable({
	providedIn: 'root',
})
export class ActivityService {
	public loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public activities$: BehaviorSubject<Activity[]> = new BehaviorSubject<Activity[]>([]);
	private readonly BASE_PATH = 'activities';

	constructor(
		private api: ApiService,
		private languageService: LanguageService,
		private contentService: ContentService
	) {}

	public async onLessonChange$(lessonUuid: string | undefined): Promise<void> {
		this.activities$.next([]);

		if (!lessonUuid) {
			return;
		}

		await this.getByLesson$(lessonUuid);
	}

	private async withLoading<T>(operation: () => Promise<T>): Promise<T> {
		this.loading$.next(true);
		try {
			return await operation();
		} finally {
			this.loading$.next(false);
		}
	}

	public setActiveText(obj: any, textKey: string): void {
		const currentLang: string = this.languageService.spokenLanguage$.value;

		if (!obj[textKey]) {
			console.warn('no text key found: ' + textKey);
			return;
		}

		const activeText = obj[textKey].find((text: BaseText) => text.default_lang_id === currentLang);

		obj.active_text = activeText || obj[textKey][0];
	}

	public processActivity(activity: Activity): Activity {
		this.setActiveText(activity, 'activity_text');
		if (activity.content && Array.isArray(activity.content)) {
			activity.content = this.contentService.processContents(activity.content);
		}
		return activity;
	}

	public processActivities(activities: Activity[]): Activity[] {
		return this.sortActivities(activities.map(activity => this.processActivity(activity)));
	}

	public async get$(uuid: string, expandChildren: boolean = false, expandAll: boolean = false): Promise<Activity> {
		if (!uuid) {
			throw new Error('Activity UUID is undefined');
		}

		return this.withLoading(async () => {
			const params = {
				expandChildren: expandChildren.toString(),
				expandAll: expandAll.toString(),
			};

			const activity = await firstValueFrom(
				this.api.get<Activity>(`${this.BASE_PATH}/get-by-uuid/${uuid}`, { params })
			);
			return this.processActivity(activity);
		});
	}

	public async create$(activityData: ActivityCreatePayload): Promise<Activity> {
		return this.withLoading(async () => {
			const newActivity = await firstValueFrom(this.api.post<Activity>(this.BASE_PATH, activityData));
			const processedActivity = this.processActivity(newActivity);
			const currentActivities = this.activities$.value;
			this.activities$.next([...currentActivities, processedActivity]);
			return processedActivity;
		});
	}

	public async update$(uuid: string, activityData: ActivityUpdatePayload): Promise<Activity> {
		return this.withLoading(async () => {
			const updatedActivity = await firstValueFrom(
				this.api.put<Activity>(`${this.BASE_PATH}/${uuid}`, activityData)
			);
			const processedActivity = this.processActivity(updatedActivity);
			const currentActivities = this.activities$.value;
			const updatedActivities = currentActivities.map(activity =>
				activity.uuid === uuid ? processedActivity : activity
			);
			this.activities$.next(updatedActivities);
			return processedActivity;
		});
	}

	public async delete$(uuid: string): Promise<void> {
		return this.withLoading(async () => {
			await firstValueFrom(this.api.delete<void>(`${this.BASE_PATH}/${uuid}`));
			const currentActivities = this.activities$.value;
			const updatedActivities = currentActivities.filter(activity => activity.uuid !== uuid);
			this.activities$.next(updatedActivities);
		});
	}

	public async getByLesson$(
		lessonUuid: string,
		expandChildren: boolean = true,
		expandAll: boolean = false
	): Promise<Activity[]> {
		return this.withLoading(async () => {
			const params = {
				lessonUuid,
				expandChildren: expandChildren.toString(),
				expandAll: expandAll.toString(),
			};

			const activities = await firstValueFrom(this.api.get<Activity[]>(`${this.BASE_PATH}/lesson`, { params }));
			const processedActivities = this.processActivities(activities);
			const sortedActivities = this.sortActivities(processedActivities);
			this.activities$.next(sortedActivities);
			return sortedActivities;
		});
	}

	public async duplicate$(uuid: string, lessonParentUuid: string): Promise<Activity> {
		return this.withLoading(async () => {
			const duplicatedActivity = await firstValueFrom(
				this.api.post<Activity>(`${this.BASE_PATH}/duplicate/${uuid}/${lessonParentUuid}`, {})
			);
			const processedActivity = this.processActivity(duplicatedActivity);
			const currentActivities = this.activities$.value;
			this.activities$.next([...currentActivities, processedActivity]);
			return processedActivity;
		});
	}

	public async duplicateBulk$(uuids: string[], lessonParentUuid: string): Promise<Activity[]> {
		return this.withLoading(async () => {
			const duplicatedActivities = await firstValueFrom(
				this.api.post<Activity[]>(`${this.BASE_PATH}/duplicate-bulk/${lessonParentUuid}`, { uuids })
			);

			const processedActivities = duplicatedActivities.map(activity => this.processActivity(activity));
			const currentActivities = this.activities$.value;
			const newUuidSet = new Set(processedActivities.map(activity => activity.uuid));
			const filteredCurrent = currentActivities.filter(activity => !newUuidSet.has(activity.uuid));
			const updatedActivities = this.processActivities([...processedActivities, ...filteredCurrent]);
			this.activities$.next(updatedActivities);

			return processedActivities;
		});
	}

	public async moveUp$(uuid: string, lessonUuid: string): Promise<Activity[]> {
		return this.withLoading(async () => {
			const updatedActivities = await firstValueFrom(
				this.api.post<Activity[]>(`${this.BASE_PATH}/move-up/${uuid}/${lessonUuid}`, {})
			);
			const processedActivities = this.processActivities(updatedActivities);
			const sortedActivities = this.sortActivities(processedActivities);
			this.activities$.next(sortedActivities);
			return sortedActivities;
		});
	}

	public async moveDown$(uuid: string, lessonUuid: string): Promise<Activity[]> {
		return this.withLoading(async () => {
			const updatedActivities = await firstValueFrom(
				this.api.post<Activity[]>(`${this.BASE_PATH}/move-down/${uuid}/${lessonUuid}`, {})
			);
			const processedActivities = this.processActivities(updatedActivities);
			const sortedActivities = this.sortActivities(processedActivities);
			this.activities$.next(sortedActivities);
			return sortedActivities;
		});
	}

	public sortActivities(activities: Activity[]): Activity[] {
		return activities.sort((a: Activity, b: Activity) => {
			if (a.display_order !== b.display_order) {
				return a.display_order - b.display_order;
			}

			if (!a.active_text || !b.active_text) {
				return 0;
			}

			return a.active_text.name.localeCompare(b.active_text.name);
		});
	}
}
